<!--
  IGNTR mobile top bar.

  Visible <lg (sidebar is off-canvas below lg). Mirrors the handoff template
  at .claude/igntr-handoff/.../shared/sidebar.component.ts: brand mark on the
  left, tenant/page label in the middle, notifications + user avatar on the
  right. The back affordance from the previous implementation is preserved
  on detail pages — it replaces the brand mark when the route is "back-eligible"
  (see BackNavigationService + initShowBackArrow).
-->
<header
  *ngIf="data$ | async as data"
  class="igntr-theme igntr-topbar fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-slate-200/70 bg-white/95 px-3 backdrop-blur"
>
  <!-- Left: back arrow on deep pages, brand mark otherwise. -->
  <button
    *ngIf="showBackArrow$ | async; else brand"
    type="button"
    (click)="backnav()"
    class="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 active:bg-slate-100"
    aria-label="Back"
    i18n-aria-label="Header back button"
  >
    <i class="pi pi-angle-left text-[16px] leading-none"></i>
  </button>
  <ng-template #brand>
    <ng-container *ngIf="!isNybolig">
      <!-- Tenant custom logo (Information tab) takes precedence over the
           default IGNTR mark when one is configured. -->
      <img
        *ngIf="customLogoUrl$ | async as customLogoUrl; else igntrMark"
        [src]="customLogoUrl"
        [alt]="tenantLabel"
        class="h-7 w-auto shrink-0"
      />
      <ng-template #igntrMark>
        <div
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white"
        >
          <igntr-flame [size]="20"></igntr-flame>
        </div>
      </ng-template>
    </ng-container>
    <img
      *ngIf="isNybolig"
      src="/assets/img/nybolig-black.svg"
      alt="Nybolig"
      class="h-7 w-auto shrink-0"
    />
  </ng-template>

  <!-- Middle: small tenant label + page title -->
  <div class="min-w-0 flex-1">
    <div
      *ngIf="!isNybolig"
      class="font-mono text-[9.5px] uppercase tracking-[0.14em] leading-none text-slate-500"
    >
      IGNTR<span *ngIf="tenantLabel"> · {{ tenantLabel }}</span>
    </div>
    <div
      *ngIf="!(isHomePage$ | async)"
      class="mt-1 truncate text-[13px] font-semibold leading-tight text-slate-900"
    >
      {{ pageTitle$ | async }}
    </div>
  </div>

  <!-- Notifications -->
  <button
    type="button"
    (click)="locked ? null : goToNotifications()"
    class="relative flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 active:bg-slate-100"
    [class.opacity-40]="locked"
    [class.cursor-not-allowed]="locked"
    [pTooltip]="lockedTooltip"
    [tooltipDisabled]="!locked"
    tooltipPosition="bottom"
    tooltipStyleClass="igntr-lock-tooltip"
    aria-label="Notifications"
    i18n-aria-label="Header's notifications button"
  >
    <igntr-icon name="bell" [size]="16"></igntr-icon>
    <span
      *ngIf="(unreadNotifications$ | async) as count"
      class="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[var(--app-theme-color)] px-1 text-[9px] font-semibold text-white"
    >
      {{ count > 9 ? '9+' : count }}
    </span>
  </button>

  <!-- User avatar — links to profile -->
  <a
    routerLink="/profile"
    class="block shrink-0 ring-2 ring-white"
    aria-label="Profile"
    i18n-aria-label="Header's profile avatar link"
  >
    <app-entity-avatar-init
      [img]="data.activeUserData.imagePath"
      [name]="data.activeUserData.userName"
      [surname]="data.activeUserData.surname"
    ></app-entity-avatar-init>
  </a>
</header>
